import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useNotifications } from "../../context/NotificationContext";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";
import Tooltip from "./Tooltip";

interface NotificationBellProps {
  /**
   * "right": the bell sits at the right of the brand-colored top bar
   * (PM efb0e798). The icon takes the bar's text color, the count badge is
   * red so it shows on any brand color, and the list opens leftward (full
   * width under the bar on a phone) so it never runs off the screen.
   */
  align?: "left" | "right";
}

const NotificationBell: React.FC<NotificationBellProps> = ({ align = "left" }) => {
  const inTopBar = align === "right";
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    browserNotificationsEnabled,
    refreshNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    removeNotification,
    requestBrowserNotificationPermission,
  } = useNotifications();

  const [permissionDismissed, setPermissionDismissed] = useState(false);
  const showPermissionPrompt =
    !browserNotificationsEnabled &&
    !permissionDismissed &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission !== "denied";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // A deliberate user action — never delayed by the automatic-refresh floor.
      refreshNotifications(true);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.is_read) {
      await markNotificationAsRead(notification.id);
    }
    if (notification.link) {
      const tenantSlug = localStorage.getItem("tenantSlug") || "";

      // Fix old broken action notification links (format: /{uuid}/staff/membership/{membershipId}...)
      const oldActionLinkMatch = notification.link.match(
        /\/[^/]+\/staff\/membership\/([^?]+)/
      );
      if (oldActionLinkMatch) {
        const membershipId = oldActionLinkMatch[1];
        navigate(`/${tenantSlug}/manage/memberships`, {
          state: { redirectedFrom: "notification", memberId: membershipId, tab: "actions" },
        });
      } else {
        // New format links include ?memberId=...&tab=... — read them and pass as state
        const url = new URL(notification.link, window.location.origin);
        const memberId = url.searchParams.get("memberId");
        const tab = url.searchParams.get("tab");
        if (memberId) {
          // Ensure tenant slug is correct in the path
          const pathParts = url.pathname.split("/").filter(Boolean);
          pathParts[0] = tenantSlug;
          navigate(`/${pathParts.join("/")}`, {
            state: { redirectedFrom: "notification", memberId, tab: tab || undefined },
          });
        } else {
          // Non-member links (e.g. CRM leads, attendance) — ensure correct tenant slug
          const pathParts = notification.link.split("?")[0].split("/").filter(Boolean);
          if (pathParts.length > 1) {
            pathParts[0] = tenantSlug;
          }
          const query = notification.link.includes("?")
            ? notification.link.substring(notification.link.indexOf("?"))
            : "";
          navigate(`/${pathParts.join("/")}${query}`);
        }
      }
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "action_assigned":
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case "action_overdue":
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case "action_completed":
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case "action_reassigned":
        return (
          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case "missing_attendance":
        return (
          <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case "payment_received":
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case "invoice_created_auto":
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case "password_reset_requested":
        return (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      // Catalog events (shared with push) — member-facing.
      case "invoice.published":
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case "autopay.failed":
      case "autopay.retry_scheduled":
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case "new_lead":
        return (
          <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        );
      case "capacity_at_risk":
        return (
          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className={`relative p-2 rounded-full transition-colors ${
          inTopBar ? "hover:bg-black/10" : isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
        }`}
        aria-label="Notifications"
      >
        <svg
          className={inTopBar ? "w-5 h-5" : "w-6 h-6 text-white"}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 w-5 h-5 text-xs font-bold rounded-full flex items-center justify-center ${
              inTopBar ? "bg-red-600 text-white" : "bg-gray-700 text-white dark:bg-gray-300 dark:text-gray-900"
            }`}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`${
            inTopBar
              ? "fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:w-80"
              : "absolute left-0 w-80"
          } mt-2 rounded-lg shadow-lg border ${
            getLightBorder(isDarkMode)
          } ${getCardBackground(isDarkMode)} z-50`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${getLightBorder(isDarkMode)}`}>
            <h3 className={`text-sm font-semibold ${getPrimaryText(isDarkMode)}`}>Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllNotificationsAsRead}
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Desktop Notification Permission Prompt */}
          {showPermissionPrompt && (
            <div className={`px-4 py-2.5 border-b ${getLightBorder(isDarkMode)} ${
              isDarkMode ? "bg-blue-900/30" : "bg-blue-50"
            }`}>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-xs ${getSecondaryText(isDarkMode)}`}>
                  Enable desktop notifications?
                </p>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={async () => {
                      await requestBrowserNotificationPermission();
                    }}
                    className="text-xs px-2.5 py-1 rounded bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] font-medium"
                  >
                    Enable
                  </button>
                  <button
                    onClick={() => setPermissionDismissed(true)}
                    className={`text-xs px-2 py-1 rounded ${
                      isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <svg
                  className={`w-12 h-12 mx-auto mb-2 ${getMutedText(isDarkMode)}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <p className={`text-sm ${getMutedText(isDarkMode)}`}>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`group w-full text-left px-4 py-3 transition-colors ${
                    !notification.is_read
                      ? isDarkMode
                        ? "bg-blue-900/20"
                        : "bg-blue-50"
                      : ""
                  } ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"} ${
                    getLightBorder(isDarkMode)
                  } border-b last:border-b-0`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${getPrimaryText(isDarkMode)} ${
                          !notification.is_read ? "font-semibold" : ""
                        }`}
                      >
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className={`text-xs mt-0.5 ${getMutedText(isDarkMode)} line-clamp-2`}>
                          {notification.message}
                        </p>
                      )}
                      <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
                        {format(parseISO(notification.created_at), "MMM d, h:mm a")}
                      </p>
                    </div>
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      {!notification.is_read && (
                        <div className="w-2 h-2 rounded-full bg-gray-700 dark:bg-gray-300" />
                      )}
                      <Tooltip content="Dismiss"><button aria-label="Dismiss"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                          isDarkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                        }`}

                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button></Tooltip>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
